"use client";

import { forwardRef, useState, type InputHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Parses user-entered numeric strings robustly. Accepts both `,` and `.` as
 * decimal separators, strips whitespace, and returns null for empty or invalid
 * input — never coerces to 0. Clamping/min-max is the caller's responsibility.
 */
export function parseUserNumber(input: string): number | null {
	if (!input || input.trim() === "") return null;
	const normalized = input.replace(",", ".").replace(/\s/g, "");
	const n = Number.parseFloat(normalized);
	return Number.isFinite(n) ? n : null;
}

function clamp(n: number, min: number | undefined, max: number | undefined): number {
	let out = n;
	if (min !== undefined && out < min) out = min;
	if (max !== undefined && out > max) out = max;
	return out;
}

export interface NumericInputProps extends Omit<
	InputHTMLAttributes<HTMLInputElement>,
	"type" | "value" | "onChange" | "inputMode"
> {
	value: number;
	onChange: (value: number) => void;
	min?: number;
	max?: number;
	step?: number;
	fallback?: number;
	suffix?: ReactNode;
	selectOnFocus?: boolean;
}

interface InputState {
	/** The raw text shown in the <input> */
	display: string;
	/** The last numeric value we emitted via onChange */
	committed: number;
}

/**
 * Locale-aware number input primitive. Accepts `,` or `.` as decimal
 * separator, uses type=text + inputMode=decimal so mobile keyboards show a
 * numeric pad without the Firefox/DE comma bug. Never silently coerces an
 * invalid entry to 0 — only re-normalizes on blur.
 */
export const NumericInput = forwardRef<HTMLInputElement, NumericInputProps>(
	(
		{
			value,
			onChange,
			min,
			max,
			step,
			fallback,
			suffix,
			selectOnFocus = true,
			className,
			onFocus,
			onBlur,
			...rest
		},
		ref,
	) => {
		const [state, setState] = useState<InputState>({ display: String(value), committed: value });
		const [isFocused, setIsFocused] = useState(false);

		// Derived-state-from-props: when the incoming value changes externally
		// (caller updated it, not us) and the field is not focused, sync the
		// display text. This is the React-recommended "setState during render"
		// pattern for deriving state from props — no useEffect needed.
		if (value !== state.committed && !isFocused) {
			setState({ display: String(value), committed: value });
		}

		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const raw = e.target.value;
			const parsed = parseUserNumber(raw);
			if (parsed === null) {
				// Keep the committed value; only update display so the user can keep
				// typing without losing their partially-entered text.
				setState((prev) => ({ ...prev, display: raw }));
				return;
			}
			const clamped = clamp(parsed, min, max);
			setState({ display: raw, committed: clamped });
			onChange(clamped);
		};

		const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
			setIsFocused(true);
			if (selectOnFocus && typeof window !== "undefined" && window.matchMedia?.("(hover: hover)").matches) {
				e.target.select();
			}
			onFocus?.(e);
		};

		const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
			setIsFocused(false);
			const parsed = parseUserNumber(state.display);
			if (parsed === null) {
				const restore = fallback ?? state.committed;
				setState({ display: String(restore), committed: restore });
				if (restore !== state.committed) {
					onChange(restore);
				}
			} else {
				const clamped = clamp(parsed, min, max);
				setState({ display: String(clamped), committed: clamped });
				if (clamped !== state.committed) {
					onChange(clamped);
				}
			}
			onBlur?.(e);
		};

		const inputEl = (
			<input
				ref={ref}
				type="text"
				inputMode="decimal"
				pattern="[0-9.,]*"
				value={state.display}
				onChange={handleChange}
				onFocus={handleFocus}
				onBlur={handleBlur}
				step={step}
				className={cn(
					"flex h-10 w-full rounded-md border border-input bg-white px-3 py-2",
					"text-base ring-offset-background",
					"placeholder:text-muted-foreground",
					"focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
					"disabled:cursor-not-allowed disabled:opacity-50",
					"md:text-sm",
					className,
				)}
				{...rest}
			/>
		);

		if (suffix === undefined || suffix === null) {
			return inputEl;
		}

		return (
			<div className="flex items-center gap-1">
				{inputEl}
				<span className="text-xs text-muted-foreground">{suffix}</span>
			</div>
		);
	},
);

NumericInput.displayName = "NumericInput";
